import React from 'react';
import { ActionButton } from 'shared/ui';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'shared/config/store/store';
import { useBlockCardMutation, useUnblockCardMutation } from '../api';
import { Snowflake } from 'shared/ui/icons';
import { useIntl } from 'react-intl';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { getAccountCards, updateCard } from 'shared/config/store/reducers/cardsSlice';
import { ApiError } from 'shared/model/types';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import {
  getEMICurrentAccount,
} from 'shared/config/store/reducers/accountsSlice';
import { CStatus } from 'entities/card/model/types';

const Freeze: React.FC = () => {
  const { formatMessage: t } = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccess, showError } = useMessage();

  const currentEmi = useSelector(getCurrentEmi);
  const currentAccount = useSelector((state: RootState) =>
    getEMICurrentAccount(state, currentEmi)
  ) as number;
  const card = useSelector(getAccountCards(currentEmi, currentAccount))?.[0];

  const isFrozen = card.status === CStatus.SoftBlocked;

  const [block] = useBlockCardMutation();
  const [unblock] = useUnblockCardMutation();

  const onUnfreeze = async () => {
    dispatch(setActivity(true));
    await unblock({ accountId: currentAccount, cardId: card.id })
      .unwrap()
      .then(({ response }) => {
        const newCard = response;
        dispatch(
          updateCard({ accountId: currentAccount, cardId: card.id, emi: currentEmi, newCard })
        );
        showSuccess( t({ id: 'freeze.unfreeze.success' }) );
      })
      .catch((err: ApiError) => {
        const error = err.data?.errors?.[0];
        showError(error?.defaultMessage ?? t({ id: 'freeze.unfreeze.error' }));
      })
      .finally(() => {
        dispatch(setActivity(false));
      });
  };

  const onFreeze = async () => {
    dispatch(setActivity(true));
    await block({ accountId: currentAccount, cardId: card.id })
      .unwrap()
      .then(({ response }) => {
        const newCard = response;
        dispatch(
          updateCard({ accountId: currentAccount, cardId: card.id, emi: currentEmi, newCard })
        );
        showSuccess( t({ id: 'freeze.freeze.success'}) );
      })
      .catch((err: ApiError) => {
        const error = err.data.errors?.[0];
        showError(error?.defaultMessage ?? t({ id: 'freeze.freeze.error'}));
      })
      .finally(() => {
        dispatch(setActivity(false));
      });
  };

  const onToggleFreeze = () => {
    if (isFrozen) {
      onUnfreeze();
    } else {
      // add modal for confirmation
      onFreeze();
    }
  };

  return (
    <ActionButton
      text={
        isFrozen
          ? t({ id: 'river.views.card.freezing.button.unfreeze' })
          : t({ id: 'river.views.card.freezing.button.freeze' })
      }
      icon={<Snowflake style={{ fontSize: 24 }} />}
      onClick={onToggleFreeze}
    />
  );
};

export default Freeze;
